<template>
    <div class="min-h-screen bg-white py-12 px-4">
        <div class="max-w-2xl mx-auto">
            <div v-if="loading" class="bg-white rounded-lg shadow-md p-8 text-center">
                <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
                <p class="text-gray-600 font-sans mb-2">Processing your donation...</p>
                <p v-if="loadingMessage" class="text-sm text-gray-500 font-sans">{{ loadingMessage }}</p>
            </div>

            <div v-else-if="error" class="bg-white rounded-lg shadow-md p-8">
                <div class="text-center">
                    <div class="text-red-500 text-5xl mb-4">✕</div>
                    <h1 class="text-2xl font-marketing font-bold text-primary mb-4">Donation Failed</h1>
                    <p class="text-gray-600 font-sans mb-6">{{ error }}</p>
                    <a href="/donate"
                        class="inline-block bg-accent text-white px-6 py-3 rounded-md hover:bg-green-600 transition-colors font-marketing font-semibold">
                        Return to Donate
                    </a>
                </div>
            </div>

            <div v-else-if="success" class="bg-white rounded-lg shadow-md p-8">
                <div class="text-center">
                    <div class="text-green-500 text-5xl mb-4">✓</div>
                    <h1 class="text-2xl font-marketing font-bold text-primary mb-4">Donation Successful!</h1>
                    <p class="text-gray-600 font-sans mb-2">Thank you for your generous donation.</p>
                    <p v-if="donationNumber" class="text-gray-600 font-sans mb-6">Donation Number: <span
                            class="font-semibold">{{ donationNumber }}</span></p>
                    <div class="flex flex-col sm:flex-row gap-4 justify-center">
                        <a href="/donate"
                            class="inline-block bg-accent text-white px-6 py-3 rounded-md hover:bg-green-600 transition-colors font-marketing font-semibold">
                            Make Another Donation
                        </a>
                        <a href="/"
                            class="inline-block bg-gray-200 text-primary px-6 py-3 rounded-md hover:bg-gray-300 transition-colors font-marketing font-semibold">
                            Return Home
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { paymentService } from '../services/payment'

const loading = ref(true)
const loadingMessage = ref(null)
const error = ref(null)
const success = ref(false)
const donationNumber = ref(null)

onMounted(async () => {
    const urlParams = new URLSearchParams(window.location.search)
    const paymentMethod = urlParams.get('payment_method')
    const token = urlParams.get('token')
    const sessionId = urlParams.get('session_id')
    const donationNumberParam = urlParams.get('donation_number')

    try {
        if (donationNumberParam) {
            await handleDonationNumberLookup(donationNumberParam)
        } else if (paymentMethod === 'paypal' && token) {
            await handlePayPalCompletion(token)
        } else if (token && !paymentMethod) {
            await handlePayPalCompletion(token)
        } else if (sessionId) {
            await handleStripeCompletion(sessionId)
        } else {
            error.value = 'Invalid payment parameters. Missing token, session ID, or donation number.'
            loading.value = false
        }
    } catch (err) {
        console.error('Donation completion error:', err)
        error.value = err.response?.data?.error || err.message || 'Failed to complete donation. Please contact support.'
        loading.value = false
    }
})

const handleDonationNumberLookup = async (donationNum) => {
    loading.value = true
    loadingMessage.value = 'Checking donation status...'
    
    try {
        const status = await paymentService.getPaymentStatus(donationNum)
        const paymentStatus = status.payment_status
        const orderStatus = status.order_status
        const receiptDonationNumber = status.order_number

        if (paymentStatus === 'paid' && orderStatus === 'completed') {
            success.value = true
            donationNumber.value = receiptDonationNumber || donationNum
            loading.value = false
            
            const url = new URL(window.location)
            url.searchParams.set('donation_number', donationNumber.value)
            window.history.replaceState({}, '', url)
        } else if (paymentStatus === 'failed') {
            error.value = 'Donation failed. Please try again or contact support if the issue persists.'
            loading.value = false
        } else if (paymentStatus === 'refunded') {
            error.value = 'This donation has been refunded. Please contact support for assistance.'
            loading.value = false
        } else {
            error.value = `Donation status: ${paymentStatus || 'unknown'}. Please contact support if you believe this is an error.`
            loading.value = false
        }
    } catch (err) {
        console.error('Donation lookup error:', err)
        const apiError = err.response?.data?.error || err.response?.data?.message
        error.value = apiError || err.message || 'Failed to check donation status. Please contact support.'
        loading.value = false
    }
}

const handlePayPalCompletion = async (token) => {
    loading.value = true
    loadingMessage.value = 'Verifying PayPal payment...'
    
    try {
        const response = await paymentService.completePayPalPayment(token)
        
        if (!response || typeof response !== 'object') {
            console.error('Invalid PayPal response:', response)
            error.value = 'Invalid response from payment server. Please contact support.'
            loading.value = false
            return
        }

        const responseSuccess = response.success
        const responseStatus = response.status
        const responseDonationNumber = response.order_number

        if (responseSuccess === true && responseStatus === 'completed') {
            success.value = true
            donationNumber.value = responseDonationNumber || null
            loading.value = false
            
            if (donationNumber.value) {
                const url = new URL(window.location)
                url.searchParams.set('donation_number', donationNumber.value)
                window.history.replaceState({}, '', url)
            }
        } else if (responseSuccess === false) {
            error.value = response.error || response.message || 'Donation could not be completed'
            loading.value = false
        } else if (responseSuccess === undefined || responseStatus === undefined) {
            console.warn('PayPal response missing required fields:', response)
            error.value = 'Donation response incomplete. Please wait a moment and refresh, or contact support.'
            loading.value = false
        } else {
            console.error('Unexpected PayPal response:', response)
            error.value = `Donation verification failed. Response status: ${responseStatus || 'unknown'}. Please contact support.`
            loading.value = false
        }
    } catch (err) {
        console.error('PayPal completion error:', err)
        console.error('Error response:', err.response?.data)
        console.error('Error status:', err.response?.status)
        
        const apiError = err.response?.data?.error || err.response?.data?.message
        error.value = apiError || err.message || 'Failed to complete PayPal donation. Please contact support.'
        loading.value = false
    }
}

const handleStripeCompletion = async (sessionId) => {
    loading.value = true
    loadingMessage.value = 'Waiting for payment confirmation...'

    const maxAttempts = 60
    const pollInterval = 2000
    let attempts = 0
    let pollTimer = null

    const pollPaymentStatus = async () => {
        try {
            attempts++
            
            const status = await paymentService.getPaymentStatus(sessionId)
            const paymentStatus = status.payment_status
            const orderStatus = status.order_status
            const receiptDonationNumber = status.order_number

            if (paymentStatus === 'paid' && orderStatus === 'completed') {
                if (pollTimer) clearInterval(pollTimer)
                loadingMessage.value = 'Payment confirmed!'
                success.value = true
                donationNumber.value = receiptDonationNumber || null
                loading.value = false
                loadingMessage.value = null
                
                if (donationNumber.value) {
                    const url = new URL(window.location)
                    url.searchParams.set('donation_number', donationNumber.value)
                    window.history.replaceState({}, '', url)
                }
            } else if (paymentStatus === 'failed') {
                if (pollTimer) clearInterval(pollTimer)
                error.value = 'Donation failed. Please try again or contact support if the issue persists.'
                loading.value = false
                loadingMessage.value = null
            } else if (paymentStatus === 'refunded') {
                if (pollTimer) clearInterval(pollTimer)
                error.value = 'This donation has been refunded. Please contact support for assistance.'
                loading.value = false
                loadingMessage.value = null
            } else if (paymentStatus === 'pending') {
                loadingMessage.value = 'Payment is processing...'
                if (attempts >= maxAttempts) {
                    if (pollTimer) clearInterval(pollTimer)
                    error.value = 'Donation is taking longer than expected. Please wait a moment and refresh the page, or contact support if the issue persists.'
                    loading.value = false
                    loadingMessage.value = null
                }
            } else {
                console.warn('Unexpected payment state:', { paymentStatus, orderStatus, status })
                loadingMessage.value = `Payment status: ${paymentStatus || 'unknown'}`
                if (attempts >= maxAttempts) {
                    if (pollTimer) clearInterval(pollTimer)
                    error.value = 'Donation is taking longer than expected. Please wait a moment and refresh the page, or contact support if the issue persists.'
                    loading.value = false
                    loadingMessage.value = null
                }
            }
        } catch (err) {
            console.error('Error polling payment status:', err)
            
            if (err.response?.status === 404 || err.response?.status === 400) {
                if (attempts >= 10) {
                    if (pollTimer) clearInterval(pollTimer)
                    error.value = 'Donation status not found. Please wait a moment and refresh the page, or contact support if the issue persists.'
                    loading.value = false
                    loadingMessage.value = null
                }
            } else if (attempts >= maxAttempts) {
                if (pollTimer) clearInterval(pollTimer)
                error.value = 'Failed to check donation status. Please refresh the page or contact support.'
                loading.value = false
                loadingMessage.value = null
            }
        }
    }

    pollPaymentStatus()
    pollTimer = setInterval(pollPaymentStatus, pollInterval)
}
</script>

<style scoped></style>
